import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Fragment, RefObject, useMemo } from "react";
import { Commodity } from "@/pages/sy/models/Commodity";
import { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import { getLayerGroupPositions } from "@/pages/sy/utils/layerGroups";
import { formatCraneMagnitude } from "../utils/formatCraneMagnitude";
import {
  getGroupBackground,
  getGroupFrameSx,
} from "@/pages/sy/utils/layerGroupFrame";
import { LayerAlignmentRow } from "../utils/layerAlignment";
import { LayerGroupHeaderRow } from "./LayerGroupHeaderRow";

export const TableRecipeLayers = ({
  commodities,
  alignedRows,
  recipeTargetWeight,
  suggestedLayerWeights,
  getCommodityColor,
  getBgColorWithAlpha,
  getRecipeLayerPercentage,
  containerRef,
  onScroll,
}: {
  commodities: Commodity[];
  alignedRows: LayerAlignmentRow[];
  recipeTargetWeight: number;
  suggestedLayerWeights?: ReadonlyMap<number, number> | null;
  getCommodityColor: (commodityId: number, commodities: Commodity[]) => string;
  getBgColorWithAlpha: (color: string, alpha: number) => string;
  getRecipeLayerPercentage: (weight: number, targetWeight: number) => number;
  containerRef?: RefObject<HTMLDivElement>;
  onScroll?: () => void;
}) => {
  const theme = useTheme();
  const styles = useMemo(() => chargeOperationStyles(theme), [theme]);

  const recipeLayers = useMemo(
    () =>
      alignedRows.flatMap(({ recipeLayer }) =>
        recipeLayer ? [recipeLayer] : [],
      ),
    [alignedRows],
  );

  const groupPositions = useMemo(
    () => getLayerGroupPositions(recipeLayers, "RecipeLayerId"),
    [recipeLayers],
  );

  const getSuggestedWeight = (layer: RecipeLayer) =>
    Number(layer.Weight ?? 0) > 0 && layer.RecipeLayerId != null
      ? suggestedLayerWeights?.get(layer.RecipeLayerId)
      : undefined;

  const renderGroupHeader = (layerNumber: number) => {
    const materials = recipeLayers.filter(
      (layer) => (layer.LayerNumber ?? 0) === layerNumber,
    );
    const percentage = materials.reduce(
      (sum, layer) =>
        sum + getRecipeLayerPercentage(layer.Weight, recipeTargetWeight),
      0,
    );
    const weight = materials.reduce(
      (sum, layer) => sum + (getSuggestedWeight(layer) ?? layer.Weight ?? 0),
      0,
    );
    return (
      <LayerGroupHeaderRow
        layerNumber={layerNumber}
        labelColSpan={2}
        values={[
          `${percentage.toFixed(1)}%`,
          formatCraneMagnitude(weight, "0"),
        ]}
      />
    );
  };

  return (
    <TableContainer
      ref={containerRef}
      onScroll={onScroll}
      sx={styles.tableContainer}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={styles.tableHeader}>
            <TableCell width="15%" align="center">
              #
            </TableCell>
            <TableCell width="50%">Commodity</TableCell>
            <TableCell width="15%" align="center">
              %
            </TableCell>
            <TableCell width="20%" align="center">
              Weight
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alignedRows.map(({ chargeLayer, recipeLayer: layer }, index) => {
            if (!layer) {
              return (
                <TableRow
                  key={`recipe-layer-gap-${chargeLayer?.ChargeLayerId ?? index}`}
                  data-layer-row
                  aria-hidden="true"
                  sx={{
                    ...(styles.tableRow as object),
                    cursor: "default",
                    backgroundColor: "white",
                    "&:hover": { backgroundColor: "white" },
                  }}
                >
                  <TableCell colSpan={4}>{"\u00A0"}</TableCell>
                </TableRow>
              );
            }

            const commodityColor = getCommodityColor(
              layer.CommodityId as number,
              commodities,
            );
            const bgColor = getBgColorWithAlpha(commodityColor, 0.1);
            const suggestedWeight = getSuggestedWeight(layer);
            const percentage = getRecipeLayerPercentage(
              layer.Weight,
              recipeTargetWeight,
            );
            const showSuggestedWeight = suggestedWeight != null;
            const groupPosition = groupPositions.get(layer.RecipeLayerId);
            const rowBackground = groupPosition ? getGroupBackground : bgColor;

            return (
              <Fragment key={layer.RecipeLayerId}>
                {groupPosition === "first" &&
                  renderGroupHeader(layer.LayerNumber ?? 0)}
                <TableRow
                  data-layer-row
                  sx={[
                    {
                      ...(styles.tableRow as object),
                      backgroundColor: rowBackground,
                      cursor: "default",
                      "&:hover": {
                        backgroundColor: rowBackground,
                      },
                    },
                    getGroupFrameSx(groupPosition),
                  ]}
                >
                  <TableCell align="center">{layer.LayerNumber}</TableCell>
                  <TableCell>
                    <Box sx={styles.commodityCell}>
                      <Box
                        sx={{
                          ...(styles.colorIndicator as object),
                          backgroundColor: commodityColor,
                        }}
                      />
                      {layer.CommodityName}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                      fontWeight: "bold",
                    }}
                  >
                    {percentage.toFixed(1)}%
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={
                      showSuggestedWeight ? { py: "0 !important" } : undefined
                    }
                  >
                    {showSuggestedWeight ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          lineHeight: 1.15,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            fontSize: "1rem",
                            textDecoration: "line-through",
                          }}
                        >
                          {formatCraneMagnitude(layer.Weight, "0")}
                        </Box>
                        <Box component="span" sx={{ fontWeight: "bold" }}>
                          {formatCraneMagnitude(suggestedWeight, "0")}
                        </Box>
                      </Box>
                    ) : layer.Weight != null ? (
                      formatCraneMagnitude(layer.Weight, "0")
                    ) : (
                      "0"
                    )}
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
